import { css } from '@emotion/react';
import { Box, COLOR_PALETTE, Flex, Skeleton, Stack } from '@inflearn/ds-react-next';
import { groupBy } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import type { UserDeviceOneResponse } from '../../../../../../resources/@types/uccSchema';
import { useDeviceDelete } from '../../../../../hooks/useDeviceDelete';
import { useUserDevicesQuery } from '../../../../../queries/useUserDevicesQuery';
import { eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../utils/GTM/useTrackMixpanelEvent';
import dayjs from '../../../../../utils/date';
import { getDeviceTypeByAgent, parseUserAgent } from '../../../../../utils/parseUserAgent';
import EmptyStates from '../../../../common/EmptyStates';
import { DeviceCautions } from './components/DeviceCautions';
import { DeviceListItem } from './components/DeviceListItem';
import { DeviceManagementHeader } from './components/DeviceManagementHeader';
import { DeviceRegisterUi } from './components/DeviceRegisterUi';

export default function DeviceManagementPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const { data, isLoading, refetch: refetchUserDevices } = useUserDevicesQuery();

  const deviceRef = useRef(parseUserAgent());
  const isDeleteApiCalled = useRef(false);

  const transformedDeviceData = data?.devices?.map(transformDeviceData) || [];
  const { true: currentDevice = [], false: otherDevices = [] } = groupBy(
    transformedDeviceData,
    (item) => item.isCurrentDevice,
  );

  const isEmpty = transformedDeviceData.length === 0;
  const hasRequestDevice = currentDevice.length > 0;

  const targetDeviceId = router.query.deviceId as string;
  const targetDevice = transformedDeviceData.find((device) => `${device.id}` === targetDeviceId);

  const { deleteDevice, verifiedCookie } = useDeviceDelete({
    deviceId: targetDevice?.id ?? 0,
    shouldRemoveQueryParam: true,
  });

  useEffect(
    function deleteTargetDevice() {
      if (isDeleteApiCalled.current) {
        return;
      }

      if (transformedDeviceData && !isEmpty && verifiedCookie && targetDevice?.id) {
        deleteDevice();
        isDeleteApiCalled.current = true;
      }
    },
    [transformedDeviceData, isEmpty, targetDevice?.id, deleteDevice, verifiedCookie],
  );

  useEffect(
    function trackViewMyDevice() {
      if (!data?.devices || isLoading) {
        return;
      }

      const isFromLecture = router.query.from === 'lecture';
      const spot = isFromLecture ? 'lecture' : 'direct';

      trackMixpanelEvent(
        eventNames.VIEW_MY_DEVICE,
        {
          device_count: String(data.devices.length),
          spot,
        },
        {
          once: true,
        },
      );
    },
    [data?.devices, isLoading, router.query.from, trackMixpanelEvent],
  );

  if (isLoading) {
    return <SkeletonUi />;
  }

  const isRegisterable = transformedDeviceData.length < 5;

  return (
    <Box>
      <Stack gap={32}>
        <DeviceCautions />

        {!hasRequestDevice && (
          <DeviceRegisterUi
            device={{
              deviceLabel: `${deviceRef.current.osName} - ${deviceRef.current.browserName}`,
              deviceType: deviceRef.current.deviceType || '',
            }}
            isForbidden={!isRegisterable}
            onRegisterSuccess={refetchUserDevices}
            deviceCount={transformedDeviceData.length}
          />
        )}

        {isEmpty ? (
          <Flex h="369px" align="center" justify="center">
            <EmptyStates
              isEmpty={true}
              empty={{
                title: t('마이페이지_기기관리_빈상태_등록없음'),
              }}
              children={<></>}
            />
          </Flex>
        ) : (
          <Box component="ul" m={0} p={0} css={{ listStyle: 'none' }}>
            {[...currentDevice, ...otherDevices].map((item) => (
              <DeviceListItem key={item.id} device={item} />
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export interface DeviceData {
  id?: number;
  deviceLabel: string;
  deviceType: string;
  lastUsedAt?: string;
  isCurrentDevice?: boolean;
}

function transformDeviceData(device: UserDeviceOneResponse): DeviceData {
  return {
    id: device.id,
    deviceLabel: `${device.osName} - ${device.clientName}`,
    deviceType: getDeviceTypeByAgent(device.userAgent),
    lastUsedAt: dayjs(device.lastAccessedAtUTC).format('L HH:mm'),
    isCurrentDevice: device.isRequesterDevice,
  };
}

export const styleRegisterable = css({
  boxShadow:
    '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 10px 15px -5px rgba(0, 0, 0, 0.05), 0 7px 7px -5px rgba(0, 0, 0, 0.04)',
});

export const styleForbidden = css({
  backgroundColor: COLOR_PALETTE.gray[0],
  border: `1px dashed ${COLOR_PALETTE.gray[3]}`,
});

const SkeletonUi = () => {
  return (
    <Box>
      <DeviceManagementHeader />
      <Stack gap={32}>
        <Stack gap={4}>
          <Skeleton h={44} w={80} />
          <Skeleton h={20} w={328} />
          <Skeleton h={20} w={300} />
        </Stack>

        <Stack gap={12}>
          <Skeleton radius="md" height={96} />
          <Skeleton radius="md" height={96} />
          <Skeleton radius="md" height={96} />
          <Skeleton radius="md" height={96} />
        </Stack>
      </Stack>
    </Box>
  );
};
